<script lang="ts">
  import { onMount } from 'svelte';
  import { fade } from 'svelte/transition';
  import AmenitiesListing from '$lib/components/AmenitiesListing.svelte';
  import CategorySelect from '$lib/components/CategorySelect.svelte';
  import Drawer from '$lib/components/Drawer.svelte';
  import LoadingScreen from '$lib/components/LoadingScreen.svelte';
  import LocationsListing from '$lib/components/LocationsListing.svelte';
  import MapView from '$lib/components/MapView.svelte';
  import SearchInput from '$lib/components/SearchInput.svelte';
  import {
    amenities,
    configLoaded,
    events,
    locationCategories,
    locations,
    mapReady,
    venue,
    hideControls,
    showDirections,
    leftMenu,
    floors,
    floorTransitions,
    mapBackgrounds,
    lighting,
    mapSuites,
    forceRoveMap,
    disableInteraction,
    enableDirections,
    hideSearch,
    forceHideAmenities,
    emitClicks,
    hideAmenities,
    loadingScreenCustomLogoUrl
  } from '$lib/stores';
  import {
    exportLanguages,
    multiLingualEnabled,
    translations,
  } from '$lib/stores/languages';
  import type { LayoutData } from './$types';
  import Directions from '$lib/components/Directions.svelte';
  import LanguageSelector from '$lib/components/LanguageSelector.svelte';

  export let data: LayoutData;

  if (data.venue?.settings?.embed?.customLoading?.logoUrl) {
    loadingScreenCustomLogoUrl.set(data.venue.settings.embed.customLoading.logoUrl);
  }

  let showLoadingScreen = true;
  let logoUrl: string | undefined = undefined;
  let logoAlt: string | undefined = undefined;

  if (data.venueExport.venue?.settings?.embed?.features?.directions === false) {
    $enableDirections = false;
  }

  const clickShowDirections = () => {
    $showDirections = !$showDirections;
  }

  let showLangSelector = false;
  const clickLangSelectorTab = () => {
    showLangSelector = true;
  };

  const closeLangSelector = () => {
    showLangSelector = false;
  };

  mapReady.subscribe((newMapReady) => {
    if (newMapReady) {
      setTimeout(() => {
        showLoadingScreen = false;
      }, 1000);
    }
  });

  const setConfigFromURLSearchParams = (searchParams:URLSearchParams):void => {
    const roveMap = searchParams.get('forceRoveMap');
    if (roveMap && roveMap === '1') {
      forceRoveMap.set(true);
    }
    const disableInteractionParam = searchParams.get('disableInteraction');
    if (disableInteractionParam && disableInteractionParam === '1') {
        disableInteraction.set(true);
    }
    const hideAmenitiesParam = searchParams.get('hideamenities')
    if (hideAmenitiesParam && hideAmenitiesParam === '1') {
      forceHideAmenities.set(true)
    }
    const hideSearchParam = searchParams.get('hidesearch')
    if (hideSearchParam && hideSearchParam === '1') {
      hideSearch.set(true)
    }
    const emitClicksParam = searchParams.get('emitclicks')
    if (emitClicksParam && emitClicksParam === '1') {
      emitClicks.set(true)
    }
  };

  onMount(() => {
    showLoadingScreen = true;
    const urlParams = new URLSearchParams(window.location.search);
    setConfigFromURLSearchParams(urlParams);

    amenities.set(data.venueExport.amenities);
    events.set(data.venueExport.events);
    locations.set(data.venueExport.locations);
    locationCategories.set(data.venueExport.locationCategories);
    if (data.venueExport.venue_languages) {
      const langModels = data.venueExport.venue_languages.map((venueLang) => venueLang.language);
      exportLanguages.set(langModels);
    }

    if (data.venueExport.venue?.settings?.embed?.defaultMapCenter?.lat) {
      data.venueExport.venue.settings.web.defaultMapCenter.lat = data.venueExport.venue.settings.embed.defaultMapCenter.lat;
    }
    if (data.venueExport.venue?.settings?.embed?.defaultMapCenter?.lng) {
      data.venueExport.venue.settings.web.defaultMapCenter.lng = data.venueExport.venue.settings.embed.defaultMapCenter.lng;
    }
    if (data.venueExport.venue?.settings?.embed?.map?.mapboxZoom) {
      data.venueExport.venue.settings.mapboxSettings.defaultZoom = data.venueExport.venue.settings.embed.map.mapboxZoom;
    }
    if (data.venueExport.venue?.settings?.embed?.map?.roveZoom) {
      data.venueExport.venue.settings.mapboxSettings.roveZoom = data.venueExport.venue.settings.embed.map.roveZoom;
    }
    if (data.venueExport.venue?.settings?.embed?.map?.pitch) {
      data.venueExport.venue.settings.mapboxSettings.defaultPitch = data.venueExport.venue.settings.embed.map.pitch;
    }
    if (data.venueExport.venue?.settings?.embed?.map?.bearing) {
      data.venueExport.venue.settings.mapboxSettings.defaultBearing = data.venueExport.venue.settings.embed.map.bearing;
    }
    if (data.venueExport.venue?.settings?.embed?.map?.mapbounds) {
      data.venueExport.venue.settings.web.mapBounds = data.venueExport.venue.settings.embed.map.mapbounds;
    }

    venue.set(data.venueExport.venue);
    if (data.venueExport.venue?.settings?.web?.logoImage) {
      logoUrl = data.venueExport.venue?.settings?.web?.logoImage;
      logoAlt = `${data.venueExport.venue.name} Logo`;
    }
    if (data.venueExport.venue?.settings?.embed?.leftMenu) {
      $leftMenu = true;
    }
    if ($venue.settings.mapType === 'rove' || $forceRoveMap) {
      floors.set(data.venueExport.floors);
      floorTransitions.set(data.venueExport.floor_transitions);
      mapBackgrounds.set(data.venueExport.map_backgrounds);
      lighting.set(data.venueExport.lighting);
      mapSuites.set(data.venueExport.map_suites);
    }

    if (data.translations && data.translations.length > 0) {
      translations.set(data.translations);
    }

    configLoaded.set(true);
  });
</script>

{#if showLoadingScreen}
  <div class="loading-screen" transition:fade={{ delay: 150, duration: 500 }}>
    <LoadingScreen />
  </div>
{/if}

<div class="map-container" class:leftMenu={$leftMenu} class:disableInteraction={$disableInteraction}>
  <MapView />
  {#if !$hideControls}
    <Drawer>
      {#if $enableDirections}
        <div id="directionsPopout"
            on:click={clickShowDirections} on:keydown={clickShowDirections}>
            <img class="h-8" alt='directions' src="/directions.svg" />
        </div>
        <Directions />
      {/if}
      {#if $multiLingualEnabled}
        <div
          id="langSelectorPopout"
          on:click={clickLangSelectorTab}
          on:keydown={clickLangSelectorTab}
        >
          <img class="h-8 lang-icon" alt="language selector" src="/language.svg" />
        </div>
        <LanguageSelector show={showLangSelector} on:close={closeLangSelector} />
      {/if}
      <div>
        <section class="logo-section">
          <div class="logo-wrapper">
            {#if logoUrl}
              <img src={logoUrl} class="logo-img" alt={logoAlt} />
            {:else}
              <h2 class="brand-title">{$venue.name}</h2>
            {/if}
          </div>
          <span
            ><img
              src="/sidebar-toggle.svg"
              class="sidebar-toggle-icon"
              alt="toggle sidebar"
            /></span
          >
        </section>
        {#if !$hideSearch}
          <section class="search-section">
            <SearchInput />
          </section>
        {/if}
        {#if !$hideAmenities}
          <section class="amenities-section">
            <AmenitiesListing />
            <div class="amenities-border-bottom" />
          </section>
        {/if}
        <section class="locations-section" class:large-height={$hideAmenities || $hideSearch}>
          <CategorySelect />
          <LocationsListing />
        </section>
      </div>
    </Drawer>
  {/if}
</div>
<slot />

<style lang="postcss">
  :global(html) {
    --main-color: #475883;
  }
  :global(.main-color-background) {
    background-color: var(--main-color);
  }

  .map-container {
    @apply w-screen h-screen flex;
  }

  .logo-section {
    @apply overflow-hidden flex flex-row items-center w-full px-8 py-4;
    height: 9vh;
    border-bottom: 1px solid #e9ebf0;
  }

  .logo-wrapper {
    @apply flex-grow;
  }

  .logo-img {
    @apply object-contain;
    max-height: 5.7vh;
  }

  .search-section {
    @apply px-8 mt-8;
  }

  .amenities-section {
    @apply h-[25vh] px-8;
  }

  .locations-section {
    @apply h-[47vh] mt-8 px-8;
  }
  .large-height {
      @apply h-[85vh];
  }
  .loading-screen {
    @apply absolute w-screen h-screen z-50 top-0;
  }

  .amenities-border-bottom {
    @apply h-6 w-full;
    border-bottom: 1px solid #e9ebf0;
  }

  :global(#directionsPopout) {
    top: 4vh;
    background-color: var(--main-color);
    right: 180px;
    @apply fixed w-auto z-50 text-white rounded-3xl flex p-2;
  }

  /* Adjustments to ensure #directionsPopout stays in line with Drawer Component */
  @media (min-width: 640px) { /* sm */
    :global(#directionsPopout) {
      right: 388px;
      @apply rounded-r-3xl;
    }
  }

  @media (min-width: 768px) { /* md */
    :global(#directionsPopout) {
      right: 390px;
      @apply rounded-r-none;
    }
  }

  @media (min-width: 1024px) { /* lg */
    :global(#directionsPopout) {
      right: 420px;
      @apply rounded-r-none;
    }
  }

  @media (min-width: 1280px) { /* xl */
    :global(#directionsPopout) {
      right: 450px;
      @apply rounded-r-none;
    }
  }
  :global(.leftMenu #directionsPopout) {
    right: unset;
    left: 26vw;
    @apply rounded-r-3xl rounded-l-none;
  }

  #moveMenu {
    position: absolute;
    top: 1px;
    left: 50vw;
  }

  .disableInteraction {
      pointer-events: none;
  }


  :global(#langSelectorPopout) {
      top: calc(10vh); /* Adjust this value to set the distance below #directionsPopout */
      right: 180px;
      @apply fixed w-12 z-50 text-white rounded-3xl flex p-2 bg-gray-800;
  }

  /* Adjustments for screen sizes to align with Drawer Component */
  @media (min-width: 640px) { /* sm */
      :global(#langSelectorPopout) {
          right: 388px;
          @apply rounded-r-3xl;
      }
  }

  @media (min-width: 768px) { /* md */
      :global(#langSelectorPopout) {
          right: 390px;
          @apply rounded-r-none;
      }
  }

  @media (min-width: 1024px) { /* lg */
      :global(#langSelectorPopout) {
          right: 420px;
          @apply rounded-r-none;
      }
  }

  @media (min-width: 1280px) { /* xl */
      :global(#langSelectorPopout) {
          right: 450px;
          @apply rounded-r-none;
      }
  }

  :global(.leftMenu #langSelectorPopout) {
      right: unset;
      left: 26vw;
      @apply rounded-r-3xl rounded-l-none;
  }

  .lang-icon {
      filter: invert(100%) brightness(100%);
  }
</style>
